// TIP-609 — the customer's Counterparty Briefs page at
// /dashboard/counterparty-briefs. TIP-611 adds ordering; the buy modal and the
// membership modal arrive separately.
// Single scrolling column reached from a sidebar item. There are no tabs in
// this product — if anyone says "the tab view", they mean the sidebar item.
// Structure is a fork, not a ladder of card variants:
//   unclaimed / pending → ClaimGate + a header-only table shell
//   the other eight     → AllowanceCard + the real BriefsTable
// Every string is the one in the flow guideline's copy appendix. Data comes
// from useCounterpartyBriefs(), which reads the live allowance endpoint (and
// fixtures under ?cbState= for review). Nothing here computes a reset date or
// holds a price, allowance or date literal.
// The fork has two arms the design never drew, because it was drawn against
// fixtures: the request in flight, and the request failing. Both come before
// the claim/product fork, and neither renders a counter — a failed read shown
// as an allowance of zero is the one wrong answer available here.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../../components/admin/Dashboard/counterpartyBriefs/counterpartyBriefs.css";
import ClaimGate from "../../../components/admin/Dashboard/counterpartyBriefs/ClaimGate";
import AllowanceCard from "../../../components/admin/Dashboard/counterpartyBriefs/AllowanceCard";
import BriefsTable from "../../../components/admin/Dashboard/counterpartyBriefs/BriefsTable";
import StatusModal from "../../../components/admin/Dashboard/counterpartyBriefs/StatusModal";
import OrderFormModal from "../../../components/admin/Dashboard/counterpartyBriefs/OrderFormModal";
import BuyBriefsModal from "../../../components/admin/Dashboard/counterpartyBriefs/BuyBriefsModal";
import MembershipModal from "../../../components/admin/Dashboard/counterpartyBriefs/MembershipModal";
import CbPanel from "../../../components/admin/Dashboard/counterpartyBriefs/CbPanel";
import CbButton from "../../../components/admin/Dashboard/counterpartyBriefs/CbButton";
import {
  BRIEF_STATUSES,
  DEEP_LINK_COPY,
  PAGE_COPY,
} from "../../../components/admin/Dashboard/counterpartyBriefs/briefsFixtures";
import {
  useBriefDetail,
  useCounterpartyBriefs,
  useBriefDeepLink,
  useBuyBriefs,
  useMembershipCheckout,
  useDownloadBrief,
  useReferenceLookup,
  useDuplicateSubjectCheck,
  useRequesterProfile,
  useSubmitBriefOrder,
} from "../../../components/admin/Dashboard/counterpartyBriefs/useCounterpartyBriefs";

const CounterpartyBriefs = () => {
  const navigate = useNavigate();
  const {
    isGate,
    gateVariant,
    allowance,
    business,
    briefs,
    statuses,
    statusCounts,
    // Present on the live path only; the ?cbState= review path leaves the
    server,
    isLoading,
    error,
    refetch,
    briefsLoading,
    briefsError,
    refetchBriefs,
  } = useCounterpartyBriefs();
  const [openBrief, setOpenBrief] = useState(null);
  const [ordering, setOrdering] = useState(false);

  // TIP-610 — the clicked row, enriched by GET /api/briefs/orders/<reference>/.
  // The fixture rows carry no live reference, so the ?cbState= path asks for
  const openBriefWithDetail = useBriefDetail(openBrief, { live: !isGate && !error });

  // TIP-611 — POST /api/briefs/orders/. Placing an order invalidates both
  // reads, so the counters and the new row arrive without anything here
  const { submit: submitOrder, isSubmitting } = useSubmitBriefOrder();

  // Section 1 of the order form. Read only while the form is open: the
  // dashboard has no reason to pull a company profile to draw a counter.
  const { profile: requesterProfile } = useRequesterProfile({ enabled: ordering });
  // Handed down to the order form, which decides when to call it. Stable, so
  // it does not restart the form's debounce on every render of this page.
  const lookupReference = useReferenceLookup();
  const checkDuplicateSubject = useDuplicateSubjectCheck();
  // A download refused because the brief is not ready means the row in hand is
  // stale, so the list is refetched rather than only apologised for.
  const brief = useDownloadBrief({ onStale: refetchBriefs });

  // TIP-613 — ?ref=TRX-CB-00007, from the brief-ready email. Resolves whose
  // brief it is, switches the active company if it belongs to another of the
  // customer's businesses, and opens it.
  const deepLink = useBriefDeepLink({ enabled: !isGate && !error });
  const [deepLinkNoticeHidden, setDeepLinkNoticeHidden] = useState(false);

  // A delivered brief goes to the DOWNLOAD, not to the status modal:
  // STATUS_MODAL_COPY has no Ready entry, so the modal renders nothing for a
  // finished brief. The other three statuses do have an explanation to show.
  // Keyed on the reference rather than the row object, so a refetch of the
  // detail does not reopen a modal the customer has closed.
  const deepLinkReady =
    deepLink.row && deepLink.row.status === BRIEF_STATUSES.READY ? deepLink.row : null;

  useEffect(() => {
    if (deepLink.row && deepLink.row.status !== BRIEF_STATUSES.READY) {
      setOpenBrief(deepLink.row);
    }
  }, [deepLink.row?.reference]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeBrief = () => {
    setOpenBrief(null);
    // Drop ?ref= so closing the modal does not leave a link that reopens it
    // on the next render, and a reload does not follow it again.
    if (deepLink.reference) navigate({ search: "" }, { replace: true });
  };

  // TIP-614 — buying extra briefs. `returnedFromCheckout` is a session id in
  // the query string, which is how Stripe sends the customer back: the modal
  // reopens on the success panel rather than dumping them on the dashboard
  // with no word about what they just paid for.
  const [buying, setBuying] = useState(false);
  const [joining, setJoining] = useState(false);
  const buy = useBuyBriefs();

  // TIP-615 — Become a Member, in this modal rather than a page away.
  const startMembership = useMembershipCheckout();
  const openMembership = () => {
    setOrdering(false);
    setBuying(false);
    setJoining(true);
  };
  useEffect(() => {
    if (buy.returnedFromCheckout) setBuying(true);
  }, [buy.returnedFromCheckout]);

  const closeBuy = () => {
    setBuying(false);
    buy.dismissPurchase();
    // Drop ONLY the query string, staying on this route. Navigating to the
    // path re-entered the route and remounted the page, which read as a full
    // reload — the counters flashed their loading state and the table
    // rebuilt. `{ search: "" }` changes the URL in place instead.
    navigate({ search: "" }, { replace: true });
    // And pull the new balance in. The poll invalidates the allowance when it
    // settles, but a purchase still crediting has invalidated nothing, so
    // closing on the crediting panel would otherwise leave the old count on
    // screen until something else happened to refetch it.
    refetch();
    refetchBriefs();
  };

  // "Find your business" has no destination in the design; the constant it
  // resolves to is a stub until the claim-flow entry point is confirmed.
  const go = (href) => {
    if (!href || href === "#") return;
    navigate(href);
  };

  return (
    <div className="cb-page">
      <div className="cb-page-header">
        <svg className="cb-page-icon" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" aria-hidden="true">
          <path className="cb-page-icon-fill" d="M6 3h9l4 4v14H6Z" />
          <path d="M15 3v4h4M9.5 12h6M9.5 16h4" />
        </svg>
        <div>
          <h1 className="cb-page-title">{PAGE_COPY.title}</h1>
          <p className="cb-page-standfirst">{PAGE_COPY.standfirst}</p>
        </div>
      </div>

      {isLoading ? (
        <CbPanel data-testid="cb-allowance-loading" variant="card">
          <p role="status" className="cb-page-state">{PAGE_COPY.loading}</p>
        </CbPanel>
      ) : error ? (
        <CbPanel data-testid="cb-allowance-error" variant="card">
          <div role="alert" className="cb-page-state">
            <p className="cb-page-state-heading">{PAGE_COPY.errorHeading}</p>
            <p>{PAGE_COPY.errorBody}</p>
          </div>
          <div className="cb-actions">
            <CbButton onClick={refetch}>{PAGE_COPY.errorRetry}</CbButton>
          </div>
        </CbPanel>
      ) : isGate ? (
        <ClaimGate variant={gateVariant} onNavigate={go} />
      ) : allowance ? (
        <>
          <AllowanceCard
            allowance={allowance}
            business={business || { name: "" }}
            onOrder={() => setOrdering(true)}
            onBuy={() => setBuying(true)}
            onBecomeMember={openMembership}
          />
          {/* Straight from the email, and the brief is finished: name it and
              offer the file. A panel rather than a highlighted row, because
              the list pages on the server and this one may be on page four. */}
          {deepLinkReady && !deepLinkNoticeHidden && (
            <div className="cb-deeplink-ready" data-testid="cb-deeplink-ready">
              <div className="cb-deeplink-ready-text">
                <p className="cb-deeplink-ready-head">
                  {DEEP_LINK_COPY.readyHeading(deepLinkReady.reference)}
                </p>
                <p className="cb-deeplink-ready-body">
                  {DEEP_LINK_COPY.readyBody(deepLinkReady.company)}
                </p>
              </div>
              <CbButton
                onClick={() => brief.download(deepLinkReady)}
                disabled={brief.downloadingRef === deepLinkReady.reference}
              >
                {DEEP_LINK_COPY.download}
              </CbButton>
            </div>
          )}

          {(deepLink.unavailable || deepLink.failed) && !deepLinkNoticeHidden && (
            <div role="alert" data-testid="cb-deeplink-notice" className="cb-deeplink-notice">
              <p className="cb-deeplink-notice-text">
                {deepLink.unavailable ? DEEP_LINK_COPY.unavailable : DEEP_LINK_COPY.failed}
              </p>
              <button
                type="button"
                className="cb-deeplink-notice-close"
                onClick={() => setDeepLinkNoticeHidden(true)}
                aria-label={DEEP_LINK_COPY.dismiss}
              >
                &#215;
              </button>
            </div>
          )}

          <BriefsTable
            briefs={briefs}
            statuses={statuses}
            statusCounts={statusCounts}
            onStatusClick={setOpenBrief}
            onOrder={() => setOrdering(true)}
            onDownload={brief.download}
            downloadingRef={brief.downloadingRef}
            downloadError={brief.error}
            onDismissDownloadError={brief.dismissError}
            isLoading={briefsLoading}
            error={briefsError}
            onRetry={refetchBriefs}
            server={server}
          />
        </>
      ) : null}

      <p className="cb-page-footer">{PAGE_COPY.footer}</p>

      <StatusModal brief={openBriefWithDetail} onClose={closeBrief} />

      {/* Mounted only while open, so the idempotency key is minted fresh each
          time the form opens and a cancelled order leaves nothing behind. */}
      {buying && (
        <BuyBriefsModal
          allowance={allowance}
          onClose={closeBuy}
          onCreateSession={buy.createSession}
          pricing={buy.pricing}
          receiptEmail={buy.receiptEmail}
          onBecomeMember={openMembership}
          onOrder={() => {
            closeBuy();
            setOrdering(true);
          }}
          purchase={
            buy.purchase
              ? {
                  ...buy.purchase,
                  // The balance after crediting, from the refreshed allowance
                  // rather than from the purchase: the customer may already
                  // have had bought briefs before this order.
                  bought: allowance?.bought ?? null,
                }
              : null
          }
          purchaseCrediting={buy.purchaseCrediting}
          purchasePending={buy.purchasePending}
          onPaid={buy.markPaid}
        />
      )}

      {joining && (
        <MembershipModal
          companyName={business?.name || allowance?.business?.name || null}
          memberUnit={allowance?.memberUnit || null}
          pricing={buy.pricing}
          receiptEmail={buy.receiptEmail}
          onCreateSession={startMembership}
          onPaid={refetch}
          onClose={() => {
            setJoining(false);
            // Membership changes the allowance (30 a month, no weekly cap),
            // and Company.is_member is derived, so the card only has to be
            refetch();
          }}
        />
      )}

      {ordering && (
        <OrderFormModal
          allowance={allowance}
          onClose={() => setOrdering(false)}
          onBuy={() => {
            setOrdering(false);
            setBuying(true);
          }}
          onSubmit={submitOrder}
          isSubmitting={isSubmitting}
          requesterProfile={requesterProfile}
          lookupReference={lookupReference}
          checkDuplicateSubject={checkDuplicateSubject}
        />
      )}
    </div>
  );
};

export default CounterpartyBriefs;
